import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import styled from '@emotion/styled';
import MinusIcon from '@icon/Syllabus/MinusIcon';
import Popper from '@material-ui/core/Popover';
import { Fonts, Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import React, { useContext } from 'react';
import PlusIcon from '@icon/Syllabus/PlusIcon';
import { makeStyles } from '@material-ui/core/styles';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useRouter } from 'next/router';
import { evtNameCategoryMap } from '@constants/index';
import { SYLLABUS_CLICKED_KEYS } from '@constants/segment';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { ClickAwayListener } from '@material-ui/core';
import {
  getIsDarkModeOn,
  getUserPreferredThemeSetter
} from '@stores/selectors/darkModeStatusSelectors';
import {
  EPUB_NOTES,
  NOTES_TEXT_SIZE_CHANGED,
  NOTES_THEME_CHANGED,
  themes
} from '../utils';
import { EpubContext } from '../Context/EpubContext';

export interface IThemePopoverFn {
  handleShowPopup: (event: any) => void;
  handleHidePopup: () => void;
}

export interface IThemePopoverProps {
  children: React.ReactNode;
  triggerRef: any;
}

interface IButton {
  $disable: boolean;
}

const useStyles = makeStyles((theme) => ({
  // popover: {
  //   pointerEvents: 'none'
  // },
  // popoverContent: {
  //   pointerEvents: 'auto'
  // }
}));

const StyledPopper = styled(Popper)`
  margin-top: ${Spacings.SPACING_4B};

  .MuiPaper-elevation8 {
    box-shadow: none;
  }

  .MuiPaper-rounded {
    border-radius: ${Spacings.SPACING_2B};
  }
`;

const Wrapper = styled.div`
  background: var(--color-base-1);
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: ${boxShadowGenerator()};
  padding: ${Spacings.SPACING_6B};
  flex-direction: column;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
`;

const Text = styled(P2)`
  color: var(--color-text-primary-light);
  font-weight: ${Fonts.HEAVY};
  margin-bottom: ${Spacings.SPACING_4B};
`;

const ColorThemeWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 100%;
  padding-bottom: ${Spacings.SPACING_4B};
  border-bottom: 1px solid var(--color-divider);
  margin-bottom: ${Spacings.SPACING_4B};
`;

const Circle = styled.div`
  min-width: ${Spacings.SPACING_8B};
  height: ${Spacings.SPACING_8B};
  border-radius: 50%;
  border: 1px solid var(--color-divider);
  cursor: pointer;
`;

const WhiteDiv = styled(Circle)`
  background: var(--color-i-white);
`;

const SelectedDiv = styled.div`
  padding: ${Spacings.SPACING_1B};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};
  border: 1px solid var(--color-divider-tertiary);
`;

const BlackDiv = styled(Circle)`
  background: var(--color-i-icon-dark-button);
`;

const SizeWrapper = styled.div`
  display: flex;
  align-items: center;
  user-select: none;
`;

const FontSizeWrapper = styled.div`
  width: 77px;
  border-radius: ${Spacings.SPACING_2B};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${Fonts.P1};
  font-weight: ${Fonts.SEMIBOLD};
  cursor: pointer;
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  height: ${Spacings.SPACING_8B};
  color: var(--color-text-primary-light);
  margin-right: ${Spacings.SPACING_4B};
  :hover {
    background-color: var(--color-base-light-0);
  }
`;

const Button = styled.div<IButton>`
  background-color: transparent;
  border-radius: 50%;
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${Spacings.SPACING_8B};
  height: ${Spacings.SPACING_8B};
  cursor: ${({ $disable }) => ($disable ? 'not-allowed' : 'pointer')};
  :hover {
    background-color: ${({ $disable }) =>
      $disable ? 'transparent' : 'var(--color-base-light-0)'};
  }
`;

const ClickAwayWrapper = styled.div``;

const MinusButton = styled(Button)`
  margin-right: ${Spacings.SPACING_4B};
`;

const stepper = 20;
const MIN_FONT_SIZE = 60;
const MAX_FONT_SIZE = 140;
const DEFAULT_FONT_SIZE = 100;

const ThemePopover = ({
  children,
  triggerRef,
  isThemePopupOpen,
  handleHidePopup
}) => {
  const {
    query: { goalUID, topicUID }
  } = useRouter();

  const { fontSize, onFontSizeChange, currentNotesUID } =
    useContext(EpubContext);

  const goalSegmentData = useGoalEventProps({ goalUID: goalUID as string });
  const setUserPreferredTheme = useDarkModeStatus(getUserPreferredThemeSetter);
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);

  const isMinusButtonDisabled = fontSize === MIN_FONT_SIZE;
  const isPlusButtonDisabled = fontSize === MAX_FONT_SIZE;

  const onThemeSelection = (selectedTheme) => () => {
    segment.track(
      NOTES_THEME_CHANGED,
      {
        ...goalSegmentData,
        last_primary_source: EPUB_NOTES,
        last_primary_source_section: EPUB_NOTES,
        notes_uid: currentNotesUID,
        notes_topic: topicUID,
        theme_selected: selectedTheme
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      SYLLABUS_CLICKED_KEYS
    );
    setUserPreferredTheme(selectedTheme === themes.DARK);
  };

  const MinusButtonClick = () => {
    if (fontSize > MIN_FONT_SIZE) {
      segment.track(
        NOTES_TEXT_SIZE_CHANGED,
        {
          ...goalSegmentData,
          last_primary_source: EPUB_NOTES,
          last_primary_source_section: EPUB_NOTES,
          notes_uid: currentNotesUID,
          notes_topic: topicUID,
          text_size: fontSize - stepper
        },
        evtNameCategoryMap.WITHOUT_CATEGORY,
        true,
        SYLLABUS_CLICKED_KEYS
      );
      onFontSizeChange(fontSize - stepper);
    }
  };

  const plusButtonClick = () => {
    if (fontSize < MAX_FONT_SIZE) {
      segment.track(
        NOTES_TEXT_SIZE_CHANGED,
        {
          ...goalSegmentData,
          last_primary_source: EPUB_NOTES,
          last_primary_source_section: EPUB_NOTES,
          notes_uid: currentNotesUID,
          notes_topic: topicUID,
          text_size: fontSize + stepper
        },
        evtNameCategoryMap.WITHOUT_CATEGORY,
        true,
        SYLLABUS_CLICKED_KEYS
      );
      onFontSizeChange(fontSize + stepper);
    }
  };

  const resetFont = () => {
    segment.track(
      NOTES_TEXT_SIZE_CHANGED,
      {
        ...goalSegmentData,
        last_primary_source: EPUB_NOTES,
        last_primary_source_section: EPUB_NOTES,
        notes_uid: currentNotesUID,
        notes_topic: topicUID,
        text_size: DEFAULT_FONT_SIZE
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      SYLLABUS_CLICKED_KEYS
    );
    onFontSizeChange(DEFAULT_FONT_SIZE);
  };

  const classes = useStyles();

  return (
    <ClickAwayWrapper>
      {children}
      <StyledPopper
        open={isThemePopupOpen}
        anchorEl={triggerRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <ClickAwayListener onClickAway={handleHidePopup}>
          <Wrapper>
            <Text>THEME</Text>
            <ColorThemeWrapper>
              {!isDarkModeOn ? (
                <SelectedDiv>
                  <WhiteDiv />
                </SelectedDiv>
              ) : (
                <WhiteDiv onClick={onThemeSelection(themes.LIGHT)} />
              )}
              {isDarkModeOn ? (
                <SelectedDiv>
                  <BlackDiv />
                </SelectedDiv>
              ) : (
                <BlackDiv onClick={onThemeSelection(themes.DARK)} />
              )}
            </ColorThemeWrapper>
            <Text>TEXT SIZE</Text>
            <SizeWrapper>
              <MinusButton
                $disable={isMinusButtonDisabled}
                onClick={MinusButtonClick}
              >
                <MinusIcon
                  size={Spacings.SPACING_6B}
                  color={
                    isMinusButtonDisabled
                      ? 'var(--color-divider)'
                      : 'var(--color-base-fill)'
                  }
                />
              </MinusButton>
              <FontSizeWrapper onClick={resetFont}>{fontSize}%</FontSizeWrapper>
              <Button onClick={plusButtonClick} $disable={isPlusButtonDisabled}>
                <PlusIcon
                  size={Spacings.SPACING_6B}
                  color={
                    isPlusButtonDisabled
                      ? 'var(--color-divider)'
                      : 'var(--color-base-fill)'
                  }
                />
              </Button>
            </SizeWrapper>
          </Wrapper>
        </ClickAwayListener>
      </StyledPopper>
    </ClickAwayWrapper>
  );
};

export default ThemePopover;
